import React, { useEffect, useState, useContext } from "react";
import App from "next/app";
import dynamic from "next/dynamic";
import Router, { withRouter } from "next/router";
import { Amplify, Auth } from "aws-amplify";
import Cookies from "js-cookie";
import * as workerTimers from "worker-timers";
import { ToastContainer } from "react-toastify";
import Head from "next/head";
import { DefaultSeo } from "next-seo";
import Script from "next/script";

import awsconfig from "./../src/config/aws.exports";
import { defaultSeo } from "../src/config/next-seo.config";
import { setJwtToken } from "../src/crud/axios";
import RedirectChecker from "./../src/common/RedirectChecker";
import AuthChecker from "./../src/common/AuthChecker";
import AuthContext from "./../src/contexts/AuthContext";

import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "./../public/css/App.css";
import "./../public/css/Styles.css";
import "../public/css/designClasses.css";
import "../public/css/RevampStaticPages.css";

// Dynamically import non-essential components
const CookiePopup = dynamic(() => import("../src/revampComponents/CookiePopup"));
const SubscribeNewsletterModal = dynamic(() => import("../src/pages/Newsletter/SubscribeNewsletterModal"));

// Initialize Amplify
Amplify.configure({ ...awsconfig, ssr: true });

class MyApp extends App {
	constructor(props) {
		super(props);
		this.state = {
			user: this.props.user || { attributes: {} },
			signedIn: this.props.signedIn || null,
			hasCookiesAccepted: !!Cookies.get("Cookie"),
			refreshTokenInterval: null
		};
	}

	async componentDidMount() {
		const queryParams = this.props.router.query;
		let refreshToken = null;

		Router.events.on("routeChangeComplete", this.authAndRedirectCheck);

		const { user, signedIn } = await AuthChecker(null, true);
		if (signedIn) {
			setJwtToken(user.signInUserSession.idToken.jwtToken);
			refreshToken = this.refreshToken();
		} else {
			Cookies.remove("authToken");
			this.props.router.replace("/", undefined, { shallow: false });
		}

		// Defer UTM storage to improve initial load
		setTimeout(() => {
			["utm_campaign", "utm_medium", "utm_source"].forEach(param => {
				if (queryParams[param]) localStorage.setItem(param, queryParams[param]);
			});
		}, 0);

		this.setState({ user, signedIn, refreshTokenInterval: refreshToken });
	}

	componentWillUnmount() {
		Router.events.off("routeChangeComplete", this.authAndRedirectCheck);
		if (this.state.refreshTokenInterval) {
			workerTimers.clearInterval(this.state.refreshTokenInterval);
		}
	}

	refreshToken = () => {
		return workerTimers.setInterval(async () => {
			try {
				const user = await Auth.currentAuthenticatedUser();
				const session = await Auth.currentSession();
				user.refreshSession(session.refreshToken, (err, session) => {
					if (!err) setJwtToken(session.idToken.jwtToken);
				});
			} catch {
				workerTimers.clearInterval(this.state.refreshTokenInterval);
				window.location.href = "/logout";
			}
		}, 60 * 60 * 1000);
	};

	authAndRedirectCheck = async () => {
		const { signedIn } = await AuthChecker(null);
		RedirectChecker(signedIn, {
			pathname: this.props.router.pathname,
			asPath: this.props.router.asPath
		});
	};

	setAuth = (user = {}, signedIn = true) => {
		if (this.state.refreshTokenInterval) {
			workerTimers.clearInterval(this.state.refreshTokenInterval);
		}
		const refreshToken = signedIn ? this.refreshToken() : null;
		this.setState({ user, signedIn, refreshTokenInterval: refreshToken });
	};

	acceptCookie = () => this.setState({ hasCookiesAccepted: true });

	render() {
		const { Component, pageProps, err } = this.props;
		const { user, signedIn, hasCookiesAccepted } = this.state;
		pageProps.signedIn = signedIn;

		return (
			<>
				<DefaultSeo {...defaultSeo} />
				<Head>
					<link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
					<link rel="manifest" href="/favicon/site.webmanifest" />
					<link rel="mask-icon" href="/favicon/safari-pinned-tab.svg" color="#5bbad5" />
					<link rel="preconnect" href="https://www.google-analytics.com" />
				</Head>
				<Script
					src="https://cdn.jsdelivr.net/npm/bootstrap@5.0.0-beta1/dist/js/bootstrap.bundle.min.js"
					integrity="sha384-ygbV9kiqUc6oa4msXn9868pTtWMgiQaeYH7/t7LECLbyPA2x65Kgf80OJFdroafW"
					crossOrigin="anonymous"
				/>
				<ToastContainer position="top-right" autoClose={5000} pauseOnHover />
				<AuthContext.Provider value={{ user, signedIn, setAuth: this.setAuth, hasCookiesAccepted, acceptCookie: this.acceptCookie }}>
					<>
						<Component {...pageProps} err={err} />
					</>
					<CookiePopup route={this.props.router.route} />
					<SubscribeNewsletterModal route={this.props.router.route} signedIn={signedIn} />
				</AuthContext.Provider>
			</>
		);
	}
}

export default withRouter(MyApp);
